import { useTranslation } from "react-i18next";
import { useParticipantSearch } from "../hooks";
import { useFieldContext } from "../form-context";
import type { BulkParticipant } from "@pulse/api/participantApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-solid-svg-icons";
import { useQuery } from "@tanstack/react-query";
import { getUsersByEmailsOptions, userByIdOptions } from "@pulse/api/queryApi";
import { MAX_PARTICIPANTS } from "../constants";
import { useMemo } from "react";
import { useEmailImport } from "./useEmailImport";
import { useCurrentCustomerId } from "@pulse/hooks/useCustomerId";
import { Button } from "frontend-components";

function ParticipantTag({ label, onRemove }: { label: string; onRemove: () => void }) {
    return (
        <div className="flex min-w-0 max-w-full items-center rounded-sm bg-secondary pl-3">
            <span className="min-w-0 max-w-64 truncate sm:max-w-80">{label}</span>
            <Button
                variant="ghost"
                size="icon-sm"
                className="shrink-0 text-on-container/80 hover:text-on-container"
                type="button"
                onClick={onRemove}
            >
                <FontAwesomeIcon icon={faXmark} />
            </Button>
        </div>
    );
}

function UserParticipantTag({ userId }: { userId: number }) {
    const field = useFieldContext<BulkParticipant[]>();
    const { data: user } = useQuery(userByIdOptions({ id: userId }));
    const handleRemove = () => {
        const currentParticipants = field.state.value || [];
        field.setValue(currentParticipants.filter((p) => !(!p.isExternal && p.userId === userId)));
    };
    return (
        <ParticipantTag label={`${user?.firstName} ${user?.lastName}`} onRemove={handleRemove} />
    );
}

function ExternalParticipantTag({ email }: { email: string }) {
    const field = useFieldContext<BulkParticipant[]>();
    const handleRemove = () => {
        const currentParticipants = field.state.value || [];
        field.setValue(currentParticipants.filter((p) => !(p.isExternal && p.email === email)));
    };
    return <ParticipantTag label={email} onRemove={handleRemove} />;
}

export function ParticipantSearchField() {
    const { t } = useTranslation();
    const { setSearch, inputRef } = useParticipantSearch();
    const field = useFieldContext<BulkParticipant[]>();
    const participants = useMemo(() => field.state.value || [], [field.state.value]);
    const customerId = useCurrentCustomerId();

    const { handlePaste, importCsvText } = useEmailImport(customerId || 0, getUsersByEmailsOptions);

    return (
        <div className="min-w-0 max-w-full">
            <div className="flex items-center pb-2 px-4 justify-between border-tertiary space-x-2">
                <span
                    className={`${participants.length > MAX_PARTICIPANTS ? "text-error font-bold" : "text-primary/50"} pb-1`}
                >
                    {/* The key exists, I18n ally doesn't pick up plurals*/}
                    {t("addParticipants.count", { count: participants.length })}
                </span>
            </div>
            <div className="min-w-0 max-w-full overflow-hidden rounded-lg border border-tertiary border-b-4 shadow-md">
                {/* Scrollable list */}
                {participants.length > 0 && (
                    <div className="flex max-h-30 min-w-0 max-w-full flex-wrap gap-2 overflow-y-auto overflow-x-hidden p-2">
                        {participants.map((participant, index) => (
                            <div
                                key={index}
                                className="flex min-w-0 max-w-full items-center rounded-sm bg-secondary"
                            >
                                {participant.isExternal ? (
                                    <ExternalParticipantTag email={participant.email} />
                                ) : participant.userId ? (
                                    <UserParticipantTag userId={participant.userId} />
                                ) : null}
                            </div>
                        ))}
                    </div>
                )}
                <input
                    type="text"
                    ref={inputRef}
                    onPaste={handlePaste}
                    placeholder={t("userSelector.searchPlaceholder")}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={async (e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            const success = await importCsvText(e.currentTarget.value);
                            if (success) inputRef.current!.value = "";
                        }
                    }}
                    className="w-full border-0 rounded-lg outline-none focus:ring-2 focus:ring-primary"
                />
            </div>
            {field.state.meta.errors.length > 0 && (
                <div className="text-error mt-1">{field.state.meta.errors[0].message}</div>
            )}
        </div>
    );
}
